import type {
  IBaseAttributes,
  IListOptions,
  IListParams,
  ListResponse,
  TConstraints,
} from '@feathr/rachis';
import { Collection, Model } from '@feathr/rachis';

/** Match types, spelled exactly as the Google Ads API enum names the backend stores. */
export enum EGoogleAdsKeywordMatchType {
  BROAD = 'BROAD',
  PHRASE = 'PHRASE',
  EXACT = 'EXACT',
}

/** Longest a sitelink's clickable label may be, per the Google Ads v22 SitelinkAsset proto. */
export const MAX_SITELINK_LINK_TEXT = 25;

/** Longest either of a sitelink's description lines may be. */
export const MAX_SITELINK_DESCRIPTION = 35;

/** Most sitelinks Google will accept on one ad group. */
export const MAX_SITELINKS_PER_AD_GROUP = 20;

export const MAX_AD_GROUP_NAME_LENGTH = 255;

/** One keyword row. Positive and negative keywords share this list, split by `negative`. */
export interface IGoogleAdsAdGroupKeyword {
  /** Stable row id, so the backend can match a row to its Google criterion across edits. */
  key: string;
  match_type: EGoogleAdsKeywordMatchType;
  negative: boolean;
  text: string;
  /** Google criterion resource name. Server-owned; never sent. */
  readonly resource_name?: string | null;
}

/** One sitelink row on an ad group, shared by every ad in the group. */
export interface IGoogleAdsAdGroupSitelink {
  /**
   * Stable row id. The backend carries a row's Google resource names forward only when its
   * key survives an edit, so dropping it discards the sitelink's performance history.
   */
  key: string;
  link_text: string;
  /** Absolute http(s) landing page. The backend rejects relative paths. */
  final_url: string;
  /** The backend requires both description lines or neither. */
  description1?: string | null;
  description2?: string | null;
  /** Google `Asset` resource name. Server-owned; never sent. */
  readonly resource_name?: string | null;
  /** Google `AdGroupAsset` resource name. Server-owned; never sent. */
  readonly link_asset_resource_name?: string | null;
}

export interface IGoogleAdsAdGroup extends IBaseAttributes {
  readonly account: string;
  readonly campaign: string;
  is_archived: boolean;
  keywords: IGoogleAdsAdGroupKeyword[];
  name: string;
  sitelinks: IGoogleAdsAdGroupSitelink[];
}

/** The sitelink rows that are filled in. A half-filled row is a placeholder the API rejects. */
export function getCompleteSitelinks(
  sitelinks: readonly IGoogleAdsAdGroupSitelink[],
): IGoogleAdsAdGroupSitelink[] {
  return sitelinks.filter(
    (sitelink) => !!sitelink.link_text?.trim() && !!sitelink.final_url?.trim(),
  );
}

/**
 * An ad group under a Google Ad Grants Search campaign. Owns the keywords and sitelinks
 * its ads share; the ads are `GoogleAdsSearchCreative`s pointing back through `ad_group`.
 */
export class GoogleAdsAdGroup extends Model<IGoogleAdsAdGroup> {
  public readonly className = 'GoogleAdsAdGroup';

  public get constraints(): TConstraints<IGoogleAdsAdGroup> {
    return {
      name: {
        presence: { allowEmpty: false, message: '^Ad group name is required.' },
        length: {
          maximum: MAX_AD_GROUP_NAME_LENGTH,
          tooLong: `^Ad group name must be ${MAX_AD_GROUP_NAME_LENGTH} characters or fewer.`,
        },
      },
      /*
       * `array` must stay the only validator here that reports per row. Our grouped
       * formatter concatenates each validator's results for one attribute rather than
       * merging them, so adding a second (`uniqueBy`, or a `length` cap) shifts every
       * index and the errors stop lining up with the rows they belong to. The sitelink
       * count and duplicate-label rules therefore live in the Ads step's own validation.
       */
      sitelinks: {
        array: {
          link_text: {
            presence: { allowEmpty: false, message: '^Link text is required.' },
            length: {
              maximum: MAX_SITELINK_LINK_TEXT,
              tooLong: `^Must be ${MAX_SITELINK_LINK_TEXT} characters or fewer.`,
            },
          },
          final_url: {
            presence: { allowEmpty: false, message: '^A URL is required.' },
            /*
             * Skipped while blank, so an untouched row reports only that a URL is
             * required. validate.js runs `url` against an empty string otherwise, and
             * the row would carry both messages at once.
             */
            url: (value: unknown) =>
              value ? { message: '^Must start with http:// or https://.' } : undefined,
          },
        },
      },
      keywords: {
        uniqueBy: {
          keys: ['text', 'match_type', 'negative'],
          message: '^Already listed with the same match type.',
        },
      },
    };
  }

  public override getDefaults(): Partial<IGoogleAdsAdGroup> {
    return {
      is_archived: false,
      keywords: [],
      sitelinks: [],
    };
  }
}

/** The endpoint requires `campaign` as a top-level param, which `IListParams` has no slot for. */
export interface IGoogleAdsAdGroupListParams extends IListParams<IGoogleAdsAdGroup> {
  campaign: string;
}

export class GoogleAdsAdGroups extends Collection<GoogleAdsAdGroup> {
  public getModel(attributes: Partial<IGoogleAdsAdGroup>): GoogleAdsAdGroup {
    return new GoogleAdsAdGroup(attributes);
  }

  public getClassName(): string {
    return 'google_ads_ad_groups';
  }

  /*
   * Ad groups live under the Google Ads integration blueprint. The trailing slash is
   * load-bearing: every per-model verb is `url() + id`.
   */
  public override url(): string {
    return `${this.getHostname()}integrations/google-ads/ad-groups/`;
  }

  /** Narrows the params so callers can't forget the required `campaign`. */
  public override list(
    params: IGoogleAdsAdGroupListParams,
    options: Partial<IListOptions> = {},
    listId?: string,
  ): ListResponse<GoogleAdsAdGroup> {
    return super.list(params, options, listId);
  }

  /**
   * The endpoint exposes PATCH but not PUT, so the inherited whole-document save would
   * 405. Routing through `patch` keeps the shared save paths working — including
   * `SaveCampaignButton`, which calls `save()` on any dirty child model on publish.
   *
   * Placeholder sitelinks are pruned here rather than at the call site, because this is
   * the one point every write passes through. `save()` does not validate, so without it
   * publishing an ad group with a half-filled row would send it and take a 400.
   */
  public override async save(
    id: string,
    patch: Partial<IGoogleAdsAdGroup> = {},
    fetchOptions: Partial<RequestInit> = {},
  ): Promise<GoogleAdsAdGroup> {
    const model = this.modelsById.get(id);
    if (!model) {
      throw new Error(`Model with id ${id} does not exist in collection`);
    }

    const attributes = { ...model.toDirtyJS(), ...patch };
    if (attributes.sitelinks) {
      attributes.sitelinks = getCompleteSitelinks(attributes.sitelinks);
    }

    return this.patch(id, attributes, { method: 'PATCH', ...fetchOptions });
  }
}
